import { Download, Email, Renew, Send } from '@carbon/icons-react';
import {
  Button,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  SkeletonText,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextArea,
  TextInput,
} from '@carbon/react';
import {
  asProblem,
  BackLink,
  FormModal,
  hasPermission,
  PageHeader,
  problemMessage,
  StatusTag,
  useAdmin,
  useFormat,
  useReferenceOptions,
} from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import {
  adjustmentDone,
  cancellationDone,
  downloadEinvoicePdf,
  einvoicesKey,
  emailEinvoice,
  getEinvoice,
  retryEinvoice,
  sendEinvoiceNow,
  setEinvoiceBuyer,
  type EinvoiceDetail,
} from './einvoiceApi';

type Dialog = 'buyer' | 'email' | 'adjusted' | 'cancelled';

/**
 * One e-invoice request (ADR 0048, FR-EIN-05): the order, the seller and buyer, what was sent and what the provider said.
 * HO can send a failed one again, send a held one now, correct a refused buyer, download the PDF, email it, and close a refund
 * flag.
 */
export function EinvoiceDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const request = useQuery({ queryKey: [...einvoicesKey, 'detail', id], queryFn: () => getEinvoice(id) });

  return (
    <section className="pos-admin-page">
      <BackLink to="/einvoice/invoices">{t('einvoice.list.back')}</BackLink>
      {request.error ? (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(request.error))} />
      ) : !request.data ? (
        <SkeletonText heading paragraph lineCount={8} />
      ) : (
        <Detail request={request.data} fetchedAt={request.dataUpdatedAt} />
      )}
    </section>
  );
}

/** @param fetchedAt When it was read: an invoice whose send time is later than that is held (ADR 0070). */
function Detail({ request, fetchedAt }: { request: EinvoiceDetail; fetchedAt: number }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const queryClient = useQueryClient();
  const stores = useReferenceOptions('org.store');
  const canManage = hasPermission(session, 'ho.einvoice.manage');
  const [dialog, setDialog] = useState<Dialog>();
  const [notice, setNotice] = useState<string>();
  const s = request.summary;
  const money = (value: number | string | null | undefined) => format.money(value, s.currencyCode);
  const statusName = t(`einvoice.status.${s.status}` as I18nKey, { defaultValue: s.status });

  const refresh = async (next?: EinvoiceDetail) => {
    if (next) queryClient.setQueryData([...einvoicesKey, 'detail', s.id], next);
    await queryClient.invalidateQueries({ queryKey: einvoicesKey });
  };
  const retry = useMutation({
    mutationFn: () => retryEinvoice(s.id),
    onSuccess: async (next) => {
      setNotice(t('einvoice.detail.retried'));
      await refresh(next);
    },
  });
  const sendNow = useMutation({
    mutationFn: () => sendEinvoiceNow(s.id),
    onSuccess: async (next) => {
      setNotice(t('einvoice.detail.sentNow'));
      await refresh(next);
    },
  });
  const pdf = useMutation({ mutationFn: () => downloadEinvoicePdf(s.id, `${s.invoiceNo ?? s.orderNumber}.pdf`) });

  const issued = s.status === 'issued';
  const canRetry = canManage && (s.status === 'failed' || s.status === 'not_configured');
  const canFixBuyer = canManage && s.status === 'failed';
  // Held until its send time (ADR 0070): it can go now instead.
  const held = s.status === 'pending' && request.nextAttemptAt != null && new Date(request.nextAttemptAt).getTime() > fetchedAt;
  const canSendNow = canManage && held;

  const facts: [string, ReactNode][] = [
    [t('einvoice.columns.order'), s.orderNumber],
    [t('einvoice.columns.store'), stores.data?.find((o) => o.value === s.storeId)?.label ?? ''],
    [t('einvoice.columns.paidAt'), format.dateTime(s.paidAt)],
    [t('einvoice.columns.total'), money(s.total)],
    [t('einvoice.detail.seller'), [request.sellerTaxCode, request.templateCode, request.invoiceSeries].filter(Boolean).join(' · ') || '—'],
    [
      t('einvoice.columns.buyer'),
      s.buyerType === 'company'
        ? [s.buyerCompanyName, request.buyerTaxCode, request.buyerAddress, request.buyerEmail].filter(Boolean).join(' · ')
        : t('einvoice.buyer.retail'),
    ],
    // The customer gave the company from the receipt's QR (FR-EIN-06, ADR 0095).
    ...(request.buyerFromReceiptAt
      ? ([[t('einvoice.detail.buyerFromReceipt'), format.dateTime(request.buyerFromReceiptAt)]] as [string, ReactNode][])
      : []),
    ...(issued
      ? ([
          [t('einvoice.columns.invoiceNo'), s.invoiceNo ?? '—'],
          [t('einvoice.detail.lookupCode'), request.lookupCode ?? '—'],
          ...(request.taxAuthorityCode ? [[t('einvoice.detail.taxAuthorityCode'), request.taxAuthorityCode]] : []),
          [t('einvoice.detail.issuedAt'), format.dateTime(s.issuedAt)],
          [t('einvoice.detail.emailedAt'), request.emailedAt ? format.dateTime(request.emailedAt) : '—'],
        ] as [string, ReactNode][])
      : []),
    ...(s.errorCode
      ? ([[t('einvoice.detail.error'), [s.errorCode, request.errorMessage].filter(Boolean).join(': ')]] as [string, ReactNode][])
      : []),
    ...(!issued && request.nextAttemptAt
      ? ([[t('einvoice.detail.nextAttempt'), format.dateTime(request.nextAttemptAt)]] as [string, ReactNode][])
      : []),
    [t('einvoice.detail.attempts'), String(request.attempts)],
    [t('einvoice.detail.transactionUuid'), request.transactionUuid],
    ...(Number(request.refundedAmount) > 0
      ? ([[t('einvoice.detail.refunded'), money(request.refundedAmount)]] as [string, ReactNode][])
      : []),
    ...(request.adjustmentNote ? ([[t('einvoice.detail.adjustmentNote'), request.adjustmentNote]] as [string, ReactNode][]) : []),
  ];

  const failure = retry.error ?? sendNow.error ?? pdf.error;
  return (
    <>
      <PageHeader
        title={s.invoiceNo ?? s.orderNumber}
        description={t('einvoice.detail.intro')}
        tag={<StatusTag status={s.status}>{statusName}</StatusTag>}
        actions={
          <>
            {issued && (
              <Button kind="tertiary" renderIcon={Download} disabled={pdf.isPending} onClick={() => pdf.mutate()}>
                {t('einvoice.detail.pdf')}
              </Button>
            )}
            {issued && canManage && (
              <Button kind="tertiary" renderIcon={Email} onClick={() => setDialog('email')}>
                {t('einvoice.detail.email')}
              </Button>
            )}
            {canFixBuyer && (
              <Button kind="secondary" onClick={() => setDialog('buyer')}>
                {t('einvoice.detail.fixBuyer')}
              </Button>
            )}
            {canSendNow && (
              <Button renderIcon={Send} disabled={sendNow.isPending} onClick={() => sendNow.mutate()}>
                {t('einvoice.detail.sendNow')}
              </Button>
            )}
            {canRetry && (
              <Button renderIcon={Renew} disabled={retry.isPending} onClick={() => retry.mutate()}>
                {t('einvoice.detail.retry')}
              </Button>
            )}
            {s.needsAdjustment && canManage && (
              <Button kind="secondary" onClick={() => setDialog('adjusted')}>
                {t('einvoice.detail.adjustmentDone')}
              </Button>
            )}
            {s.needsCancellation && canManage && (
              <Button kind="secondary" onClick={() => setDialog('cancelled')}>
                {t('einvoice.detail.cancellationDone')}
              </Button>
            )}
          </>
        }
      />
      {s.needsCancellation && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('einvoice.detail.needsCancellationTitle')}
          subtitle={t('einvoice.detail.needsCancellationBody')}
        />
      )}
      {s.status === 'not_reissued' && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={t('einvoice.detail.notReissuedTitle')}
          subtitle={t('einvoice.detail.notReissuedBody')}
        />
      )}
      {s.needsAdjustment && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('einvoice.detail.needsAdjustmentTitle')}
          subtitle={t('einvoice.detail.needsAdjustmentBody', { amount: money(request.refundedAmount) })}
        />
      )}
      {notice && <InlineNotification kind="success" role="status" lowContrast title={notice} onClose={() => setNotice(undefined)} />}
      {failure && (
        <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(failure))} />
      )}

      <StructuredListWrapper isCondensed aria-label={s.orderNumber}>
        <StructuredListBody>
          {facts.map(([label, value]) => (
            <StructuredListRow key={label}>
              <StructuredListCell noWrap>{label}</StructuredListCell>
              <StructuredListCell>{value}</StructuredListCell>
            </StructuredListRow>
          ))}
        </StructuredListBody>
      </StructuredListWrapper>

      {request.lines.length > 0 && (
        <TableContainer title={t('einvoice.detail.lines')} description={t('einvoice.detail.linesIntro')}>
          <Table aria-label={t('einvoice.detail.lines')}>
            <TableHead>
              <TableRow>
                <TableHeader>{t('einvoice.lines.name')}</TableHeader>
                <TableHeader>{t('einvoice.lines.quantity')}</TableHeader>
                <TableHeader>{t('einvoice.lines.amount')}</TableHeader>
                <TableHeader>{t('einvoice.lines.rate')}</TableHeader>
                <TableHeader>{t('einvoice.lines.tax')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {request.lines.map((line, index) => {
                const less = line.kind === 'discount' ? '−' : '';
                return (
                  <TableRow key={index}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell>{line.quantity == null ? '' : format.number(line.quantity)}</TableCell>
                    <TableCell>{less + money(line.amountWithoutTax)}</TableCell>
                    <TableCell>{line.taxRate == null ? t('einvoice.lines.noVat') : `${format.number(line.taxRate)}%`}</TableCell>
                    <TableCell>{less + money(line.taxAmount)}</TableCell>
                  </TableRow>
                );
              })}
              {request.taxBreakdowns.map((b, index) => (
                <TableRow key={`rate-${index}`}>
                  <TableCell>
                    {t('einvoice.lines.atRate', {
                      rate: b.taxRate == null ? t('einvoice.lines.noVat') : `${format.number(b.taxRate)}%`,
                    })}
                  </TableCell>
                  <TableCell />
                  <TableCell>{money(b.taxableAmount)}</TableCell>
                  <TableCell />
                  <TableCell>{money(b.taxAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialog === 'buyer' && (
        <BuyerDialog
          request={request}
          onClose={() => setDialog(undefined)}
          onSaved={async (next) => {
            setDialog(undefined);
            setNotice(t('einvoice.detail.retried'));
            await refresh(next);
          }}
        />
      )}
      {dialog === 'email' && (
        <EmailDialog
          request={request}
          onClose={() => setDialog(undefined)}
          onSent={async (next) => {
            setDialog(undefined);
            setNotice(t('einvoice.detail.emailed'));
            await refresh(next);
          }}
        />
      )}
      {(dialog === 'adjusted' || dialog === 'cancelled') && (
        <AdjustedDialog
          cancelled={dialog === 'cancelled'}
          request={request}
          onClose={() => setDialog(undefined)}
          onDone={async (next) => {
            setDialog(undefined);
            await refresh(next);
          }}
        />
      )}
    </>
  );
}

/** Correct the buyer the provider refused, then send it again (FR-EIN-05). */
function BuyerDialog({
  request,
  onClose,
  onSaved,
}: {
  request: EinvoiceDetail;
  onClose(): void;
  onSaved(next: EinvoiceDetail): Promise<void>;
}) {
  const { t } = useTranslation();
  const [buyerType, setBuyerType] = useState(request.summary.buyerType);
  const [companyName, setCompanyName] = useState(request.summary.buyerCompanyName ?? '');
  const [taxCode, setTaxCode] = useState(request.buyerTaxCode ?? '');
  const [address, setAddress] = useState(request.buyerAddress ?? '');
  const [email, setEmail] = useState('');
  const company = buyerType === 'company';
  const save = useMutation({
    mutationFn: () =>
      setEinvoiceBuyer(request.summary.id, {
        buyerType,
        companyName: company ? companyName.trim() : null,
        taxCode: company ? taxCode.trim() : null,
        address: company ? address.trim() : null,
        // The saved address is masked here: empty keeps it.
        email: company && email.trim() ? email.trim() : null,
      }),
    onSuccess: onSaved,
  });

  return (
    <FormModal
      title={t('einvoice.detail.fixBuyer')}
      label={request.summary.orderNumber}
      primaryText={t('einvoice.detail.saveAndSend')}
      pending={save.isPending}
      error={save.error ? problemMessage(t, asProblem(save.error)) : null}
      onSubmit={() => save.mutate()}
      onClose={onClose}
    >
      <RadioButtonGroup
        legendText={t('einvoice.columns.buyer')}
        name="einvoice-buyer-type"
        valueSelected={buyerType}
        onChange={(value) => setBuyerType(String(value))}
      >
        <RadioButton id="einvoice-buyer-company" labelText={t('einvoice.buyer.company')} value="company" />
        <RadioButton id="einvoice-buyer-retail" labelText={t('einvoice.buyer.retail')} value="retail" />
      </RadioButtonGroup>
      {company && (
        <>
          <TextInput
            id="einvoice-buyer-name"
            labelText={t('einvoice.buyer.companyName')}
            value={companyName}
            maxLength={400}
            required
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <TextInput
            id="einvoice-buyer-tax"
            labelText={t('einvoice.buyer.taxCode')}
            value={taxCode}
            maxLength={20}
            required
            onChange={(e) => setTaxCode(e.target.value)}
          />
          <TextInput
            id="einvoice-buyer-address"
            labelText={t('einvoice.buyer.address')}
            value={address}
            maxLength={1200}
            required
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextInput
            id="einvoice-buyer-email"
            type="email"
            labelText={t('einvoice.buyer.email')}
            helperText={t('einvoice.buyer.emailKept')}
            value={email}
            maxLength={500}
            onChange={(e) => setEmail(e.target.value)}
          />
        </>
      )}
    </FormModal>
  );
}

/** Email the issued invoice: to the buyer's address, or to one typed here. */
function EmailDialog({
  request,
  onClose,
  onSent,
}: {
  request: EinvoiceDetail;
  onClose(): void;
  onSent(next: EinvoiceDetail): Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => emailEinvoice(request.summary.id, email.trim() || null), onSuccess: onSent });
  return (
    <FormModal
      title={t('einvoice.detail.email')}
      label={request.summary.invoiceNo ?? request.summary.orderNumber}
      primaryText={t('einvoice.detail.send')}
      pending={send.isPending}
      error={send.error ? problemMessage(t, asProblem(send.error)) : null}
      onSubmit={() => send.mutate()}
      onClose={onClose}
    >
      <TextInput
        id="einvoice-email"
        type="email"
        labelText={t('einvoice.buyer.email')}
        helperText={
          request.buyerEmail ? t('einvoice.detail.emailToBuyer', { email: request.buyerEmail }) : t('einvoice.detail.emailNeeded')
        }
        value={email}
        maxLength={500}
        onChange={(e) => setEmail(e.target.value)}
      />
    </FormModal>
  );
}

/**
 * HO adjusted the refunded invoice at the provider, or cancelled the voided order's invoice there (ADR 0135): close the flag with what was
 * done.
 */
function AdjustedDialog({
  cancelled,
  request,
  onClose,
  onDone,
}: {
  cancelled: boolean;
  request: EinvoiceDetail;
  onClose(): void;
  onDone(next: EinvoiceDetail): Promise<void>;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const done = useMutation({
    mutationFn: () => (cancelled ? cancellationDone : adjustmentDone)(request.summary.id, note.trim()),
    onSuccess: onDone,
  });
  const empty = note.trim() === '';
  return (
    <FormModal
      title={cancelled ? t('einvoice.detail.cancellationDone') : t('einvoice.detail.adjustmentDone')}
      label={request.summary.invoiceNo ?? request.summary.orderNumber}
      pending={done.isPending}
      error={done.error ? problemMessage(t, asProblem(done.error)) : null}
      onSubmit={() => {
        setSubmitted(true);
        if (!empty) done.mutate();
      }}
      onClose={onClose}
    >
      <TextArea
        id="einvoice-adjustment-note"
        labelText={t('einvoice.detail.adjustmentNote')}
        helperText={t('einvoice.detail.adjustmentNoteHelp')}
        value={note}
        maxLength={500}
        required
        invalid={submitted && empty}
        invalidText={t('admin.validation.required')}
        onChange={(e) => setNote(e.target.value)}
      />
    </FormModal>
  );
}
